import { useTranslation } from 'react-i18next'

import {
  DangerButton,
  HStack,
  PrimaryButton,
  TertiaryButton,
} from '@enechain/ecloud-designsystem'

import { Modal } from '~/components/common/Modal'

type Props = {
  isOpen: boolean
  title: string
  /**
   * ダイアログ本文。プレーン文字列に加え、内訳リスト等の構造化コンテンツ
   * (React.ReactNode) も渡せる (例: Supply48DataCsvUpload 専用画面の
   * 確定ダイアログが売買 × カテゴリ × 件数の内訳を表示する、AC-10)。
   */
  body: React.ReactNode
  confirmLabel: string
  /**
   * 確認ボタンの文字の左に置くアイコン。 確定でファイルを保存するダイアログは
   * ダウンロードアイコンを渡す (UI 辞書 components.md「CSV 導線の文言・アイコン規約」、
   * 2026-10-27 デザイナー決定)。
   */
  confirmIcon?: React.ReactNode
  /**
   * true のとき確認ボタンを DangerButton (= 削除系) で表示する。
   * false / 未指定のとき通常の PrimaryButton。
   */
  destructive?: boolean
  onConfirm: () => void
  onCancel: () => void
  isLoading?: boolean
  /**
   * true のとき確認ボタン (destructive の DangerButton / 通常の PrimaryButton)
   * を描画しない。 削除失敗時のエラー表示など「もう再実行できない状態」を
   * `cancelLabel` の切替と組み合わせて表現するために使う。
   */
  hideConfirm?: boolean
  /**
   * キャンセル / 閉じるボタンの label 上書き。 省略時は `common:action.cancel`
   * (キャンセル)。 `hideConfirm` を伴うエラー表示モードでは caller が
   * `common:action.close` (閉じる) を渡すことを想定。
   */
  cancelLabel?: string
}

/**
 * 共通確認ダイアログ。
 *
 * 削除確認など「本当に実行しますか？」系の操作に使う。
 * destructive={true} で確認ボタンが DangerButton になる。
 * 15 タブ × CRUD の Delete で再利用することを前提に設計。
 */
export const ConfirmDialog = ({
  isOpen,
  title,
  body,
  confirmLabel,
  confirmIcon,
  destructive = false,
  onConfirm,
  onCancel,
  isLoading = false,
  hideConfirm = false,
  cancelLabel,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const footer = (
    <HStack gap={3}>
      <TertiaryButton onClick={onCancel}>
        {cancelLabel ?? t('common:action.cancel')}
      </TertiaryButton>
      {!hideConfirm &&
        (destructive ? (
          <DangerButton loading={isLoading} onClick={onConfirm}>
            {confirmIcon}
            {confirmLabel}
          </DangerButton>
        ) : (
          <PrimaryButton loading={isLoading} onClick={onConfirm}>
            {confirmIcon}
            {confirmLabel}
          </PrimaryButton>
        ))}
    </HStack>
  )

  return (
    <Modal
      footer={footer}
      isOpen={isOpen}
      onClose={onCancel}
      size="sm"
      title={title}
    >
      <div>{body}</div>
    </Modal>
  )
}
